import { downloadInvoiceAsPdf, downloadInvoiceDraftAsPdf } from "../../common/invoiceDownloadHelper";

// Enums for invoice filtering
enum InvoicePurposeType {
    General = 1
}

enum InvoiceStatus {
    UnPaid = 1,
    Paid = 2,
    PartlyPaid = 3,
    Overdue = 4
}

enum InvoiceStage {
    Sent = 1,
    Completed = 2,
    Pending = 3
}
// Normalizes ',' (Swedish decimal separator) to '.', caps decimals at maxDecimals, and returns null for invalid input
export const sanitizePriceInput = (value: string, maxDecimals: number = 2): string | null => {
    if (value === '') {
        return value;
    }

    const normalized = value.replace(',', '.');

    if (!/^\d*\.?\d*$/.test(normalized)) {
        return null;
    }

    const dotIndex = normalized.indexOf('.');
    if (dotIndex !== -1 && normalized.length - dotIndex - 1 > maxDecimals) {
        return normalized.slice(0, dotIndex + 1 + maxDecimals);
    }

    return normalized;
};

export const formatAmount = (num: number) => {
    const isSwedish = new URLSearchParams(window.location.search).get('lang') === 'sv-se';
    return new Intl.NumberFormat(isSwedish ? 'sv-SE' : 'en-US').format(num);
};

/**
 * Format amount with locale-specific number formatting
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'SEK')
 * @param locale - Locale string (default: current language from localStorage)
 * @returns Formatted amount string with exactly 2 decimal places
 * 
 * Examples:
 * - sv-SE: 28 000,09 SEK
 * - en-US: 28,000.09 SEK
 */
export const formatAmountWithLocale = (
    amount: number,
    currency: string = 'SEK',
    locale?: string
): string => {
    // Get locale from localStorage if not provided
    const currentLocale = locale || localStorage.getItem('i18nextLng') || 'en-US';

    // Normalize locale string (sv-se -> sv-SE, en-us -> en-US)
    const normalizedLocale = currentLocale.includes('sv') ? 'sv-SE' : 'en-US';

    // Format with exactly 2 decimal places
    const formattedNumber = new Intl.NumberFormat(normalizedLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Math.abs(amount));

    return `${formattedNumber} ${currency}`;
};

export const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('sv-SE');
};

export const mapInvoiceStatus = (status: number): string => {
    switch (status) {
        case 2:
            return 'PAID';
        case 3:
            return 'PARTLY_PAID';
        case 4:
            return 'OVERDUE';
        default:
            return 'UNPAID';
    }
};
export const mapSalaryStatus = (status: number): string => {
    switch (status) {
        case 2:
            return 'PAID';
        case 3:
            return 'PARTLY_PAID';
        case 4:
            return 'OVERDUE';
        default:
            return 'UNPAID';
    }
};

export const getStatusBadgeClass = (status: number): string => {
    switch (status) {
        case 2:
            return 'invoice-table-detailed__badge--paid';
        case 4:
            return 'invoice-table-detailed__badge--overdue';
        case 3:
            return 'invoice-table-detailed__badge--partly-paid';
        case 1:
        default:
            return 'invoice-table-detailed__badge--unpaid';
    }
};

export const getStatusForSalaryStatus = (status: number): string => {
    switch (status) {
        case 1:
            return 'invoice-table-detailed__badge--paid';
        case 0:
        default:
            return 'invoice-table-detailed__badge--unpaid';
    }
};

export const getStageBadgeClass = (stage?: string | number): string => {
    // Handle string stage names
    if (typeof stage === 'string') {
        const upperStage = stage.toUpperCase();
        switch (upperStage) {
            case 'SENT':
                return 'invoice-table-detailed__stage-badge--sent';
            case 'COMPLETED':
                return 'invoice-table-detailed__stage-badge--completed';
            case 'PENDING':
                return 'invoice-table-detailed__stage-badge--pending';
            default:
            return  'invoice-table-detailed__stage-badge--pending';
        }
            
    }

    // Handle numeric stage values
    switch (stage) {
        case InvoiceStage.Sent:
        case 1:
            return 'invoice-table-detailed__stage-badge--sent';
        case InvoiceStage.Completed:
        case 2:
            return 'invoice-table-detailed__stage-badge--completed';
        case InvoiceStage.Pending:
        case 3:
        default:
            return 'invoice-table-detailed__stage-badge--pending';
    }
};



export const getExpressPaymentStatus = (invoice: any, isLoginWithBankId: boolean, t: any ) => {
    let title = "";
    let isDisabled = false;
    let iconName = ['Approved', 'NotApproved', 'NotApplied']
    let icon = iconName[2]
    const paymentTerm = invoice?.customer?.paymentTerm || invoice?.paymentTerm;
    const accountType = invoice?.customer?.accountType ?? invoice?.accountType;
    const country = invoice?.customer?.country || invoice?.country;

    if (!isLoginWithBankId) {
        console.log("isLoginWithBankId:", isLoginWithBankId);
        title = t("EXPRESS_PAYMENT_LOGIN_BANKID") || "To access express payment, please log in with BankID.";
        isDisabled = true;
        icon = iconName[2]
    } else if (paymentTerm === "45" || paymentTerm === "60") {
        title = t("EXPRESS_PAYMENT_45_60_DAYS") || "We don't accept express payment for 45 & 60 days.";
        isDisabled = true;
        icon = iconName[2]
    } else if (accountType === 1) {
        title = t("EXPRESS_PAYMENT_NO_PRIVATE") || "We don't accept express payment for private customers.";
        isDisabled = true;
        icon = iconName[2]
    } else if (country !== "Sweden" && accountType !== 1) {
        title = t("EXPRESS_PAYMENT_SWEDEN_ONLY") || "We don't accept express payments for customers outside Sweden.";
        isDisabled = true;
        icon = iconName[2]
    } else if (invoice?.amount > 60000) {
        title = t("EXPRESS_PAYMENT_LIMIT_60K") || "For Express Payment, there is a limit of SEK 60,000 per invoice.";
        isDisabled = true;
        icon = iconName[2]
    } else {
        title = t("EXPRESS_PAYMENT") || "Express Payment";
        isDisabled = false;
        icon = iconName[0]
    }

    return { title, isDisabled , icon };
};


export const handleDownloadInvoice = async (invoice: any, lang: string) => {
    try {
        await downloadInvoiceAsPdf(invoice, lang);
    } catch (error: any) {
        console.error('Download error:', error);
        alert(`Failed to download invoice: ${error.message}`);
    }
};

export const handleDownloadDraftInvoice = async (invoice: any, lang: string) => {
    try {
        await downloadInvoiceDraftAsPdf(invoice, lang);
    } catch (error: any) {
        console.error('Download error:', error);
        alert(`Failed to download invoice: ${error.message}`);
    }
};


export const formatDateForPayout = (
    prefillDate: string | number | Date,
    paymentTerm: boolean,
    language: string | undefined
) => {
    const currentDate = prefillDate ? new Date(prefillDate) : new Date();

    if (paymentTerm) {
        currentDate.setDate(currentDate.getDate() + Number(paymentTerm));
    }

    // Fix: language check should compare with actual values, not with an uninitialized variable
    const lang = language === 'sv-se' ? 'sv-se' : 'en-us';

    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
    };

    return currentDate.toLocaleString(lang, options);
};


export const isGuestInvozioEmail = (email: string) => {
    try {
        const lowerEmail = email.toLowerCase();
        return lowerEmail.startsWith('guest') && lowerEmail.endsWith('@invozio.com');
    } catch (error: string | any) {
        console.error("Error checking guest email:", error.message);
        return false;
    }
};

export const sentInvoicesStatus = (invoice: any): string => {
    if (invoice.isEmailSent && invoice.invoiceStatus === InvoiceStatus.UnPaid && !invoice.isSalaryCreated) {
        return 'SENT';
    } else if (
        invoice.isEmailSent &&
        invoice.invoiceStatus !== InvoiceStatus.UnPaid &&
        (invoice.isSalaryCreated || invoice.isSalaryStatusUpdatedManually || invoice.invoiceSalaryStatus === InvoiceStatus.Paid)
    ) {
        return 'COMPLETED';
    } else if (!invoice.isEmailSent) {
        return 'PENDING';
    }

    return 'PENDING';
};
